import * as React from "react"

import { cn } from "../../cn"

/**
 * The invisible strip a pointer drags. Wider than the border it straddles so
 * the target is comfortable, and marked `group` so the grip can react to hover,
 * focus, and an in-progress drag.
 */
export const resizeHandleClassName =
  "group absolute top-0 z-10 h-full w-3 cursor-col-resize bg-transparent"

/** Pixels one arrow key moves a resize separator. */
const RESIZE_KEYBOARD_STEP_PX = 24

/**
 * Pointer and keyboard handlers for a focusable `role="separator"` on a pane's
 * left edge: ArrowLeft widens the pane and ArrowRight narrows it. A focusable
 * separator is a widget under ARIA, so it takes these handlers directly.
 */
export function resizeSeparatorHandlers({
  width,
  setWidth,
  onPointerDown,
}: {
  width: number
  setWidth: (width: number) => void
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
}) {
  return {
    onPointerDown,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        setWidth(width + RESIZE_KEYBOARD_STEP_PX)
      }
      if (event.key === "ArrowRight") {
        event.preventDefault()
        setWidth(width - RESIZE_KEYBOARD_STEP_PX)
      }
    },
  }
}

/** Wrapper spanning the handle, so the parts below can centre on its border. */
const gripBoxClassName = "pointer-events-none absolute inset-0"

/** Which way the divider itself runs between the two panes. */
export type ResizeGripOrientation = "vertical" | "horizontal"

const gripLineToneClassName = cn(
  "z-20 bg-transparent",
  "transition-colors duration-150",
  "group-hover:bg-coval-border-strong",
  "group-focus-visible:bg-coval-border-strong",
  "group-data-[resizing=true]:bg-coval-border-strong",
)

/**
 * The border between the two panes, redrawn on top of the pane's own so it can
 * darken while the divider is live without the pane having to know about it.
 */
const gripLineClassName: Record<ResizeGripOrientation, string> = {
  vertical: cn("absolute inset-y-0 left-1/2 w-px", gripLineToneClassName),
  horizontal: cn("absolute inset-x-0 top-1/2 h-px", gripLineToneClassName),
}

const gripPillToneClassName = cn(
  "absolute z-[21] rounded-[2.5px] bg-coval-border-strong",
  "opacity-0 transition-opacity duration-150",
  "group-hover:opacity-100 group-focus-visible:opacity-100",
  "group-data-[resizing=true]:opacity-100",
)

/**
 * The grabber: a pill riding the middle of the border between two panes, in the
 * same tone the border takes while the divider is live. It stays hidden until
 * the divider is hovered, focused, or being dragged, so a resting page shows
 * nothing but the border itself.
 *
 * The line it rides is the 1px starting at the midpoint, so the pill shifts
 * half a pixel along the cross axis to sit concentric with it.
 */
const gripPillClassName: Record<ResizeGripOrientation, string> = {
  vertical: cn(
    "left-[calc(50%+0.5px)] top-1/2 h-[36px] w-[5px]",
    "-translate-x-1/2 -translate-y-1/2",
    gripPillToneClassName,
  ),
  horizontal: cn(
    "top-[calc(50%+0.5px)] left-1/2 w-[36px] h-[5px]",
    "-translate-x-1/2 -translate-y-1/2",
    gripPillToneClassName,
  ),
}

export type ResizeGripProps = React.HTMLAttributes<HTMLSpanElement> & {
  /** Defaults to a vertical divider, the one between side-by-side panes. */
  orientation?: ResizeGripOrientation
}

/**
 * Affordance for a drag-to-resize divider: a border that darkens and a grabber
 * that appears while the divider is live. Render it inside the drag handle —
 * the handle owns the pointer behaviour, this only draws the affordance.
 */
const ResizeGrip = React.forwardRef<HTMLSpanElement, ResizeGripProps>(
  ({ className, orientation = "vertical", ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden
      className={cn(gripBoxClassName, className)}
      {...props}
    >
      <span className={gripLineClassName[orientation]} />
      <span className={gripPillClassName[orientation]} />
    </span>
  ),
)

ResizeGrip.displayName = "ResizeGrip"

/**
 * Imperative equivalent of `<ResizeGrip />`, for handles built as raw DOM —
 * react-split hands us a bare element to fill in rather than a React tree.
 */
export function createResizeGrip(
  orientation: ResizeGripOrientation = "vertical",
): HTMLSpanElement {
  const box = document.createElement("span")
  box.className = gripBoxClassName
  box.setAttribute("aria-hidden", "true")

  for (const partClassName of [
    gripLineClassName[orientation],
    gripPillClassName[orientation],
  ]) {
    const part = document.createElement("span")
    part.className = partClassName
    box.appendChild(part)
  }

  return box
}

export { ResizeGrip }
